import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { customerApi, GeoAutomationScene } from '../services/customerApi';
import { Viewer } from '@ydrive/gs-viewer';
import PathNetworkMiniMap from '../components/PathNetworkMiniMap';
import MeasureTool from '../components/MeasureTool';
import { fetchManifest, findSegmentForPOI, ManifestData, localToWGS84 } from '../utils/manifestUtils';
//import poiPinIcon from '../assets/poi-pin.svg';
import POIOverlay, { POIData } from '../components/POIOverlay';
import AssetFilterPanel from '../components/AssetFilterPanel';
import SourceImages from '../components/SourceImages';
import { usePoiFlythrough } from '../hooks/usePoiFlythrough';
import PoiNavigationOverlay from '../components/PoiNavigationOverlay';
import { mtm8ToGeodetic } from '../utils/measureUtils';
import { I18nProvider, useI18n } from '../i18n';


// Load logo from public folder
const logo = '/logo.gif';

// Function to get geo-coordinates for starting position based on scene ID
const getGeoCoordinatesForScene = (sceneId: string) => {
  switch (sceneId.toLowerCase()) {      
    case 'terrebonne':
      return {
        latitude: 45.711666715372324,
        longitude: -73.63830042963465,
        altitude: 58.856383377944255,
        heading: 0
      };
    case 'officespace':
      return {
        latitude: 45.5017,
        longitude: -73.5673,
        altitude: 2,
        heading: 0
      };
    default:
      return {
        latitude: 0,
        longitude: 0,
        altitude: 0,
        heading: 0
      };
  }
};

const GeoAutomationViewer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang, t, toggle } = useI18n();
  
  const geoScene = searchParams.get('scene');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scene, setScene] = useState<GeoAutomationScene | undefined>(undefined);
  const [showNavPanel, setShowNavPanel] = useState(false); // Show navigation panel
  const [isViewerReady, setIsViewerReady] = useState(false); // True only when .gm is fully loaded & geodetic position is valid
  const [assetsVisible, setAssetsVisible] = useState(false);
  const [assetFilterOpen, setAssetFilterOpen] = useState(false);
  
  const viewerRef = useRef<any>(null);
  const viewerContainerRef = useRef<HTMLDivElement | null>(null);
  const readyPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Map state
  const [pathNetwork, setPathNetwork] = useState<number[][][]>([]);
  const [manifest, setManifest] = useState<ManifestData | null>(null);
  const [currentSegment, setCurrentSegment] = useState<number | null>(null);
  const [cameraPosition, setCameraPosition] = useState<{
    latitude: number;
    longitude: number;
    altitude: number;
    heading: number;
  } | null>(null);
  const [pois, setPois] = useState<POIData[]>([]);
  const [measureActive, setMeasureActive] = useState(false);
  const measureBtnRef = useRef<HTMLButtonElement>(null);
  const assetsBtnRef = useRef<HTMLButtonElement>(null);
  const [activeLayers, setActiveLayers] = useState<Set<string>>(new Set());

  // Segment loading state — fires when the user walks ahead of the stream
  const [isSegmentLoading, setIsSegmentLoading] = useState(false);
  const [loadingSegmentIndex, setLoadingSegmentIndex] = useState<number | null>(null);
  const originalMovementSpeedRef = useRef<number>(5); // restored when segment finishes
  const originalMaxClickDistRef = useRef<number>(15);
  const originalLookSpeedRef     = useRef<number>(10);

  // POI flythrough — segment helpers so the hook can wait for "truly arrived"
  const getCurrentSegment = useCallback(() => currentSegment, [currentSegment]);
  const getManifestSegmentForPoi = useCallback((poi: POIData) => {
    if (!manifest) return null;
    const [lat, lon, alt] = poi.points?.[0]
      ? (() => {
          const p = poi.points![0];
          if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z)) {
            return [poi.latitude, poi.longitude, poi.altitude] as const;
          }
          const geo = mtm8ToGeodetic(p.x, p.y, p.z);
          return [geo.latitude, geo.longitude, geo.altitude] as const;
        })()
      : [poi.latitude, poi.longitude, poi.altitude] as const;
    return findSegmentForPOI(lat, lon, alt, manifest);
  }, [manifest]);
 
  const {
  flyTo,
  flyToGeoPoint,
  isNavigating,
  navigatingToPoiName,
  selectedPoiId,
  setSelectedPoiId,
  isNavigatingRef,
} = usePoiFlythrough({
  viewerRef,
  pois,
  pathNetwork,
  speedRefs: {
    originalMovementSpeedRef,
    originalLookSpeedRef,
    originalMaxClickDistRef,
  },
  getCurrentSegment,
  getManifestSegmentForPoi,
});

const getPoiGeoPosition = useCallback((poi: POIData | undefined) => {
  if (!poi) return undefined;

  if (poi.points?.[0]) {
    const p = poi.points[0];
    if (Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)) {
      const geo = mtm8ToGeodetic(p.x, p.y, p.z);
      return {
        latitude: geo.latitude,
        longitude: geo.longitude,
      };
    }
  }

  return {
    latitude: poi.latitude,
    longitude: poi.longitude,
  };
}, []);

  // Snapshot camera position at the moment navigation starts so the overlay
  // minimap can show the journey from where the user actually was.
  const navFromPositionRef = useRef<{ latitude: number; longitude: number } | null>(null);
  const [mapNavTarget, setMapNavTarget] = useState<{ latitude: number; longitude: number } | null>(null);

  useEffect(() => {
  if (!isNavigating) {
      navFromPositionRef.current = null;
      setMapNavTarget(null);
    }
  }, [isNavigating]);


  // Wrap flyTo so we snapshot the REAL camera position synchronously at click time,
  // before any async work starts. getCameraGeodeticPosition() is always current.
  const handlePoiMapClick = useCallback((id: string) => {
    try {
      const pos = viewerRef.current?.getCameraGeodeticPosition();
      if (pos && Number.isFinite(pos.lat) && Number.isFinite(pos.lon)) {
        navFromPositionRef.current = { latitude: pos.lat, longitude: pos.lon };
      }
    } catch {
      // viewer not ready — fromPosition stays as last known value
    }
    flyTo(id);
  }, [flyTo, viewerRef]);

    const handleMiniMapClick = useCallback((point: { latitude: number; longitude: number }) => {
      try {
        const pos = viewerRef.current?.getCameraGeodeticPosition();
        if (pos && Number.isFinite(pos.lat) && Number.isFinite(pos.lon)) {
          navFromPositionRef.current = { latitude: pos.lat, longitude: pos.lon };
        }
      } catch {}

      setMapNavTarget({ latitude: point.latitude, longitude: point.longitude }); // ← add this

      flyToGeoPoint({
        latitude: point.latitude,
        longitude: point.longitude,
        name: t.poiNav.selectedPoint,
      });
    }, [flyToGeoPoint]);

  const selectedPoi = selectedPoiId ? pois.find(p => p.id === selectedPoiId) : undefined;
  const navToPosition = getPoiGeoPosition(selectedPoi);

  const [sourceImagesActive, setSourceImagesActive] = useState(false);
  const sourceImagesBtnRef = useRef<HTMLButtonElement>(null);

  // Tracks every layer name we have ever presented to the user, so we can
  // distinguish "brand new layer → default on" from "user unchecked → keep off".
  // A ref avoids adding it to the effect dependency array (which would loop).
  const knownLayersRef = useRef<Set<string>>(new Set());

  // Reconcile activeLayers against the current POI set on every POI change:
  //   • remove stale names no longer in the incoming set
  //   • add brand-new names (never seen before) as on by default
  //   • preserve checked/unchecked state for names that already existed
  useEffect(() => {
    if (pois.length === 0) return;
    const incoming = new Set(pois.map(p => p.layerName ?? '(unlabelled)'));
    setActiveLayers(prev => {
      const next = new Set<string>();
      incoming.forEach(name => {
        // checked before → stay checked
        // brand new (not in knownLayers) → default on
        // known but unchecked → stay off (don't add)
        if (prev.has(name) || !knownLayersRef.current.has(name)) next.add(name);
      });
      return next;
    });
    knownLayersRef.current = incoming;
  }, [pois]);

  // filteredPois is always synchronous — no effect, no timing gap
  const filteredPois = useMemo(
    () => pois.filter(p => activeLayers.has(p.layerName ?? '(unlabelled)')),
    [pois, activeLayers]
  );

  const handleToggleLayer = useCallback((name: string) => {
    setActiveLayers(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }, []);

  const showAllAssetLayers = useCallback(() => {
    setActiveLayers(new Set(pois.map(p => p.layerName ?? '(unlabelled)')));
  }, [pois]);

  const handleShowOnlyLayer = useCallback((name: string) => {
    setActiveLayers(new Set([name]));
  }, []);

  const handleToggleAll = useCallback(() => {
    const allNames = new Set(pois.map(p => p.layerName ?? '(unlabelled)'));
    setActiveLayers(prev => prev.size === allNames.size ? new Set() : allNames);
  }, [pois]);

  const handleAssetFilterOk = useCallback(() => {
    setAssetFilterOpen(false);
  }, []);

  const handleAssetFilterClose = useCallback(() => {
    setAssetFilterOpen(false);
    setAssetsVisible(false);
    setSelectedPoiId(null);
  }, []);

  // Fetch scene data
  useEffect(() => {
    let isActive = true; // Each effect run gets its own flag
    const fetchAndLoadScene = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsViewerReady(false); // Reset readiness on new scene load

        if (!geoScene) {
          throw new Error(t.viewer.errorMissingScene);
        }

        console.log('[GeoViewer] Fetching scene for geoScene:', geoScene);
        const response = await customerApi.getGeoAutomationSceneBySceneId(geoScene);
        if (!isActive) {
          console.log('[GeoViewer] ⚠️ Effect cleaned up, ignoring API response');
          return;
        }

        if (response.success && response.data) {
          const sceneData = response.data;
          console.log('[GeoViewer] Loaded scene:', sceneData.sceneName);
          
          if (!sceneData.manifest) {
            throw new Error(t.viewer.errorNoManifest(sceneData.sceneName));
          }

          // Fetch manifest for coordinate conversion
          try {
            const manifestData = await fetchManifest(sceneData.manifest);
            setManifest(manifestData);
            console.log('[GeoViewer] Manifest loaded with', manifestData.segment_count, 'segments');
          } catch (err) {
            console.warn('[GeoViewer] Could not load manifest:', err);
          }
          
          setScene(sceneData);
          if (!viewerContainerRef.current) {
            console.error('[GeoViewer] Viewer container not mounted');
            setError(t.viewer.errorInit);
            setLoading(false);
            return;
          }
          loadScene(sceneData, () => isActive);
        } else {
          throw new Error(t.viewer.errorNotFound(geoScene));
        }
      } catch (err) {
        console.error('[GeoViewer] Error fetching scene:', err);
        if (isActive) {
          setError(err instanceof Error ? err.message : 'Failed to load scene');
          setLoading(false);
        }
      }
    };

    fetchAndLoadScene();
    //clean up when component unmounts or re-renders
      return () => {
      isActive = false;
      console.log('[GeoViewer] Cleaning up effect');
      
      // Dispose viewer on unmount
      if (viewerRef.current && typeof viewerRef.current.dispose === 'function') {
        try {
          console.log('[GeoViewer] Disposing viewer on unmount');
          viewerRef.current.dispose();
          viewerRef.current = null;
        } catch (error) {
          console.warn('[GeoViewer] Error disposing on unmount:', error);
        }
      }
        if (viewerContainerRef.current) {
          viewerContainerRef.current.innerHTML = '';
        }
     
      // Clear readiness poll if still running
      if (readyPollRef.current) {
        clearInterval(readyPollRef.current);
        readyPollRef.current = null;
      }
    };
  }, [geoScene]);

  // Load path network
  useEffect(() => {
    if (!scene) return;

    const loadPathNetwork = async () => {
      try {
        let pathUrl = '';
        
        switch (scene.sceneId.toLowerCase()) {
          case 'terrebonne':
            pathUrl = '/path-networks/terrebonne_path_8km.json';
            break;
          // Add other scenes when needed
        }

        if (pathUrl) {
          const response = await fetch(pathUrl);
          const pathData = await response.json();
          setPathNetwork(pathData);
          console.log('[GeoViewer] Loaded path network:', pathData.length, 'segments');
        }
      } catch (error) {
        console.error('[GeoViewer] Error loading path network:', error);
      }
    };

    loadPathNetwork();
    
    return () => {
      setPathNetwork([]); // Clear on unmount
    };
  }, [scene]);

  // Track camera position
  useEffect(() => {
    const updateCameraPosition = () => {
      try {
        // Read REAL camera position from viewer
        if (viewerRef.current?.camera && manifest) {
          const camera = viewerRef.current.camera;
          const origin = manifest.observer_coordinates;
          
          // Get camera position in local coords (yzx mapping)
          const localX = camera.position.z;
          const localY = camera.position.x;
          const localZ = camera.position.y;
          
          // Convert to WGS84
          const wgs84 = localToWGS84(localX, localY, localZ, origin);
          
          const position = {
            latitude: wgs84.latitude,
            longitude: wgs84.longitude,
            altitude: wgs84.altitude,
            heading: 0
          };
          
          setCameraPosition(position);
          
          // Update segment (only log changes)
          const segment = findSegmentForPOI(
            position.latitude,
            position.longitude,
            position.altitude,
            manifest
          );
          
          if (segment !== currentSegment) {
            setCurrentSegment(segment);
            
          }
          
          return;
        }
        
        // FALLBACK: Use URL params if camera not ready yet
        const latParam = searchParams.get('lat');
        const lonParam = searchParams.get('lon');
        const altParam = searchParams.get('alt');
        const headingParam = searchParams.get('heading');
        
        let position;
        
        if (latParam && lonParam) {
          position = {
            latitude: parseFloat(latParam),
            longitude: parseFloat(lonParam),
            altitude: altParam ? parseFloat(altParam) : 1.5,
            heading: headingParam ? parseFloat(headingParam) : 0
          };
        } else if (geoScene) {
          const defaultCoords = getGeoCoordinatesForScene(geoScene);
          position = {
            latitude: defaultCoords.latitude,
            longitude: defaultCoords.longitude,
            altitude: defaultCoords.altitude,
            heading: defaultCoords.heading
          };
        }
        
        if (position) {
          setCameraPosition(position);
        }
        
      } catch (error) {
        // Silent fail
      }
    };

    updateCameraPosition();
    const interval = setInterval(updateCameraPosition, 1000); // Update every 1 second
    return () => clearInterval(interval);
  }, [manifest]); // Only manifest dependency

  // Load 3D scene 
  const loadScene = (sceneToLoad: GeoAutomationScene, checkIsActive: () => boolean) => {
    try {
      console.log('[GeoViewer] Initializing viewer...');
      console.log('[GeoViewer] Manifest:', sceneToLoad.manifest);

      if (!viewerContainerRef.current) {
        console.error('[GeoViewer] Viewer container not mounted');
        if (checkIsActive()) {
          setError(t.viewer.errorInit);
          setLoading(false);
        }
        return;
      }
      
      // Cleanup
      if (viewerRef.current && viewerRef.current !== 'cleaning') {
        try {
          viewerRef.current.dispose();
        } catch (error) {
          console.warn('[GeoViewer] Error disposing:', error);
        }
        viewerRef.current = null;
      }

      if (viewerContainerRef.current) {
        viewerContainerRef.current.innerHTML = '';
      }
      
           
      // Create viewer -  NO enableOptimizations
      const viewerOptions: any = {
        rootElement: viewerContainerRef.current,
        sharedMemoryForWorkers: false,
        cameraFar: 100000,
        disableSplatTree: false,
        cameraFoV: 60,
        logLevel: 4,
        maxClickToMoveDistance: 15,// metres horizontal
        
      };

      console.log('[GeoViewer] Creating viewer...');
      const viewer = new Viewer(viewerOptions) as any;
      viewerRef.current = viewer;

      
      // Get coordinates from URL parameters OR fallback to hardcoded defaults
      const latParam = searchParams.get('lat');
      const lonParam = searchParams.get('lon');
      const altParam = searchParams.get('alt');
      const headingParam = searchParams.get('heading');
      
      let geoCoords;

      // Typical car eye level height (driver's seat)
      const CAR_EYE_LEVEL = 1.5; // meters
      
      if (latParam && lonParam) {
        // Use URL parameters
        geoCoords = {
          latitude: parseFloat(latParam),
          longitude: parseFloat(lonParam),
          altitude: altParam ? parseFloat(altParam) : CAR_EYE_LEVEL,
          heading: headingParam ? parseFloat(headingParam) : 0  // Use URL heading or default to 0
        };
        console.log('[GeoViewer] Using coordinates from URL:', geoCoords);
      } else {
        // Fallback to hardcoded coordinates
        geoCoords = getGeoCoordinatesForScene(sceneToLoad.sceneId);
        // Override altitude if provided in URL
        if (altParam) {
          geoCoords.altitude = parseFloat(altParam);
        }
        // Override heading if provided in URL
        if (headingParam) {
          geoCoords.heading = parseFloat(headingParam);
        }
        console.log('[GeoViewer] Using hardcoded coordinates:', geoCoords);
      }

      // Segment loading callbacks — freeze all input on entry, restore on load complete.
      // Skipped entirely while POI navigation is active to avoid restore-order clashes.
      const onSegmentLoadingStart = (segmentIndex: number) => {
        if (isNavigatingRef.current) return;
        console.log(`[GeoViewer] 🔴 Segment ${segmentIndex} entered but still loading — freezing controls`);
        const v = viewerRef.current;
        if (v?.controls) {
          originalMovementSpeedRef.current = v.controls.movementSpeed ?? 5;
          originalLookSpeedRef.current     = v.controls.lookSpeed     ?? 10;
          v.controls.movementSpeed = 0.3;
          v.controls.lookSpeed     = 0;
        }
        if (v) {
          originalMaxClickDistRef.current = v.maxClickToMoveDistance ?? 15;
          v.maxClickToMoveDistance = 0;
          v.clickTransitioning        = false;
          v.transitioningCameraTarget = false;
          if (v.nextCamera)     v.nextCamera.copy(v.camera);
          if (v.previousCamera) v.previousCamera.copy(v.camera);
        }
        setLoadingSegmentIndex(segmentIndex);
        setIsSegmentLoading(true);
      };
 
      const onSegmentLoadingEnd = (segmentIndex: number) => {
        if (isNavigatingRef.current) return;
        console.log(`[GeoViewer] 🟢 Segment ${segmentIndex} loaded — restoring controls`);
        const v = viewerRef.current;
        if (v?.controls) {
          v.controls.movementSpeed = originalMovementSpeedRef.current;
          v.controls.lookSpeed     = originalLookSpeedRef.current;
        }
        if (v) {
          v.maxClickToMoveDistance = originalMaxClickDistRef.current;
        }
        setIsSegmentLoading(false);
        setLoadingSegmentIndex(null);
      };
      
      // Scene options 
      const sceneOptions: any = {
        showLoadingUI: false,
        latitude: geoCoords.latitude,
        longitude: geoCoords.longitude,
        altitude: geoCoords.altitude,
        heading: geoCoords.heading,
        onSegmentLoadingStart,
        onSegmentLoadingEnd, 
      };
 
      console.log('[GeoViewer] Scene options:', sceneOptions);
 

     
      // Load manifest 
      viewer.addSplatScenesFromManifest(sceneToLoad.manifest, sceneOptions)
      .then(async (result: any) => {
        if (!checkIsActive()) {
          console.log('[GeoViewer]  Effect cleaned up, ignoring success callback');
          return;
        }
        console.log('[GeoViewer] manifest loaded:', result);
        viewer.start();
        // --- POI Markers in 3d ---
        // Fetch POIs from backend and drop pins using geoPosition (viewer handles coordinate conversion internally)
        if (geoScene) {
          try {
            console.log('[GeoViewer] Fetching POIs for scene:', geoScene);
            const poiResponse = await customerApi.getGeoAutomationPOIs(geoScene);
            const pois = poiResponse?.data ?? [];
            console.log('[GeoViewer] POIs received:', pois.length, pois);
            setPois(pois.map((p: any) => ({
              id: p.id ?? String(p.name),
              name: p.name,
              latitude: p.latitude,
              longitude: p.longitude,
              altitude: p.altitude ?? 0,
              description: p.description,
              layerName: p.layerName,   
              points: p.points,         
            })));
            console.log('[GeoViewer] ✅ POIs loaded (React overlay only, no 3D markers)');
          } catch (poiErr) {
            // Non-fatal – viewer still works without POIs
            console.warn('[GeoViewer] Could not load POIs:', poiErr);
          }
        }
        // --- end POI Markers in 3d ---
        
       console.log('[GeoViewer] Viewer started');

         //hide loading screen, 3D scene becomes visible
          setLoading(false);
          console.log('[GeoViewer] 🎬 Loading screen hidden, 3D scene visible');

          // Poll until getCameraGeodeticPosition() returns valid (non-zero) coordinates.
          // The manifest promise resolves before all .gm splat data is uploaded to the GPU,
          // so we wait until the viewer can actually report a real geodetic position.
          if (readyPollRef.current) clearInterval(readyPollRef.current);
          readyPollRef.current = setInterval(() => {
            try {
              if (!viewerRef.current) {
                clearInterval(readyPollRef.current!);
                readyPollRef.current = null;
                return;
              }
              const pos = viewerRef.current.getCameraGeodeticPosition();
              const hasValidCoords =
                pos &&
                typeof pos.lat === 'number' &&
                typeof pos.lon === 'number' &&
                (pos.lat !== 0 || pos.lon !== 0) &&
                isFinite(pos.lat) &&
                isFinite(pos.lon);

              if (hasValidCoords) {
                const segmentLoaded = !viewerRef.current.isLoadingOrUnloading();
                if (!segmentLoaded) return; // keep polling until segment finishes
                console.log('[GeoViewer] ✅ Viewer ready – geodetic position valid, segment loaded:', pos);
                setIsViewerReady(true);
                setShowNavPanel(true);
                clearInterval(readyPollRef.current!);
                readyPollRef.current = null;
              }
            } catch (e) {
              // getCameraGeodeticPosition not ready yet – keep polling
            }
          }, 500);

      })
  .catch((err: any) => {
    if (!checkIsActive()) {
      console.log('[GeoViewer] ⚠️ Effect cleaned up, ignoring error callback');
      return;
    }
    console.error('[GeoViewer] ❌ Error:', err);
    setError(t.viewer.errorLoad3D);
    setLoading(false);
  });
      
    } catch (error) {
      console.error('[GeoViewer] ❌ Error in loadScene:', error);
      if (checkIsActive()) {
        setError(t.viewer.errorInit);
        setLoading(false);
      }
    }
  };

 
  const handleBackTo2D = () => {
    console.log('[GeoViewer] Back to 2D button clicked');
    
    try {
      if (viewerRef.current) {
        // Get GPS coordinates from viewer
        const geodeticPos = viewerRef.current.getCameraGeodeticPosition();
        
        console.log('[GeoViewer] Raw geodetic position:', geodeticPos);
        
        // Transform property names: lat → latitude, lon → longitude, alt → altitude
        const gpsPosition = {
          latitude: geodeticPos.lat,
          longitude: geodeticPos.lon,
          altitude: geodeticPos.alt,
          heading: geodeticPos.heading ?? 0
        };
        
        console.log('[GeoViewer] Transformed GPS position:', gpsPosition);
        console.log('[GeoViewer] Heading:', gpsPosition.heading, '° (0=North, 90=East, 180=South, 270=West)');
        
        // Send GPS coordinates to parent
        window.parent.postMessage({
          type: 'BACK_TO_2D',
          data: { 
            gpsPosition: gpsPosition,
            sceneId: geoScene 
          }
        }, '*');
        
        console.log('[GeoViewer] Sent GPS coordinates to parent');
      } else {
        console.warn('[GeoViewer] Viewer not available');
        
        window.parent.postMessage({
          type: 'BACK_TO_2D',
          data: { 
            gpsPosition: null,
            sceneId: geoScene 
          }
        }, '*');
      }
    } catch (error) {
      console.error('[GeoViewer] Error getting GPS position:', error);
      
      window.parent.postMessage({
        type: 'BACK_TO_2D',
        data: { 
          gpsPosition: null,
          sceneId: geoScene,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }, '*');
    }
  };

  // Hide nav panel - only on backdrop click
  const handleBackdropClick = () => {
    if (!isViewerReady) return;
    setShowNavPanel(false);
  };

  // Panel click also dismisses once ready
  const handlePanelClick = () => {
    if (!isViewerReady) return;
    setShowNavPanel(false);
  };

  
// Main viewer UI
  return (
   <>
      <div
        ref={viewerContainerRef}
        className="fixed inset-0 bg-black"
        style={{ zIndex: 0, overflow: 'hidden' }}
      />

      {error && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-[9999]">
          <div className="text-center text-white max-w-md px-8">
            <h2 className="text-2xl font-semibold mb-4">{t.viewer.errorTitle}</h2>
            <p className="text-white/70 mb-6">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="bg-[#0C5484] text-white px-6 py-3 rounded-lg hover:bg-[#0CB4E4]/90 transition-colors"
            >
              {t.viewer.errorBack}
            </button>
          </div>
        </div>
      )}

      {loading && !error && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-[9999]">
          <div className="text-center text-white">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-[#0CB4E4]" />
            <p className="text-lg">{t.viewer.loading3D}</p>
          </div>
        </div>
      )}
      {!error && (
        <>
          {/* ── DEBUG: segment loading callout — top-left ── */}
          <div className="fixed z-[2000] pointer-events-none"
            style={{ top: 80, left: '50%', transform: 'translateX(-50%)' }}>
            {isSegmentLoading && (
              <div className="px-4 py-2 rounded-full"
                style={{
                  background: 'rgba(0,0,0,0.55)',
                  backdropFilter: 'blur(6px)',
                  color: 'rgba(255,255,255,0.92)',
                  fontSize: 12,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.02em',
                }}>
                {t.viewer.segmentLoading}
              </div>
            )}
          </div>
          {/* POI flythrough navigation overlay */}
          <PoiNavigationOverlay
            isNavigating={isNavigating}
            poiName={navigatingToPoiName}
            pathNetwork={pathNetwork}
            fromPosition={navFromPositionRef.current ?? undefined}
            toPosition={navToPosition ?? mapNavTarget ?? undefined}
          />
          
          {/* Language toggle — top-left */}
          <div className="fixed top-4 left-4 z-[1002]">
            <button
              onClick={toggle}
              onMouseDown={e => e.preventDefault()}
              className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold tracking-widest shadow-lg transition-all duration-200"
              style={{
                background: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(8px) saturate(140%)',
                WebkitBackdropFilter: 'blur(8px) saturate(140%)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'white',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <span style={{ opacity: lang === 'fr' ? 1 : 0.4 }}>FR</span>
              <span style={{ opacity: 0.3 }}>|</span>
              <span style={{ opacity: lang === 'en' ? 1 : 0.4 }}>EN</span>
            </button>
          </div>

          {/* Top-right button row: Assets + Measure + Image View */}
          <div className="fixed top-4 right-4 z-[1002] flex items-center gap-2">
            {/* Assets button — toggles POI pins on minimap + scene */}
            {isViewerReady && !showNavPanel && (
              <button
                ref={assetsBtnRef}
                onClick={() => {
                  const next = !assetFilterOpen;
                  if (next) {
                    showAllAssetLayers();
                    setAssetsVisible(true);
                    setAssetFilterOpen(true);
                    setMeasureActive(false);
                    setSourceImagesActive(false);
                  } else {
                    handleAssetFilterClose();
                  }
                }}
                className="flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-200 shadow-lg"
                style={{
                  background:     (assetsVisible || assetFilterOpen) ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(8px) saturate(140%)',
                  WebkitBackdropFilter: 'blur(8px) saturate(140%)',
                  border:     (assetsVisible || assetFilterOpen) ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.1)',
                  color:          'white' ,
                  fontWeight: 600,
                  
                  cursor: 'pointer',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 21s-8-6.5-8-12a8 8 0 0 1 16 0c0 5.5-8 12-8 12z"/>
                  <circle cx="12" cy="9" r="2.5"/>
                </svg>
                <span>{t.viewer.assetsBtn}</span>
              </button>
            )}

            {/* SourceImages button — only shown after segment loaded AND nav panel dismissed */}
            {isViewerReady && !showNavPanel && (
              <button
                ref={sourceImagesBtnRef}
                onClick={() => {
                  const next = !sourceImagesActive;
                  setSourceImagesActive(next);
                  if (next) { setMeasureActive(false); setAssetFilterOpen(false); setAssetsVisible(false); setSelectedPoiId(null); }
                }}
                className="flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-200 shadow-lg"
                style={{
                  background: sourceImagesActive ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(8px) saturate(140%)',
                  WebkitBackdropFilter: 'blur(8px) saturate(140%)',
                  border: sourceImagesActive ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.1)',
                  color: 'white',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>{t.viewer.sourceImagesBtn}</span>
              </button>
            )}

            {/* Measure button — only shown after segment loaded AND nav panel dismissed */}
            {isViewerReady && !showNavPanel && (
              <button
                ref={measureBtnRef}
                onClick={() => {
                  const next = !measureActive;
                  setMeasureActive(next);
                  if (next) { setSourceImagesActive(false); setAssetFilterOpen(false); setAssetsVisible(false); setSelectedPoiId(null); }
                }}
                className="flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-200 shadow-lg"
                style={{
                  background: measureActive ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(8px) saturate(140%)',
                  WebkitBackdropFilter: 'blur(8px) saturate(140%)',
                  border: measureActive ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.1)',
                  color:  'white' ,
                  fontWeight: 600,
                  
                  cursor: 'pointer',
                }}
              >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="8" width="20" height="8" rx="1.5"/>
                <line x1="6"  y1="8" x2="6"  y2="13"/>
                <line x1="10" y1="8" x2="10" y2="11"/>
                <line x1="14" y1="8" x2="14" y2="13"/>
                <line x1="18" y1="8" x2="18" y2="11"/>
              </svg>
                <span>{t.viewer.measureBtn}</span>
              </button>
            )}

            {/* Image View button */}
            <button
              onClick={isViewerReady ? handleBackTo2D : undefined}
              disabled={!isViewerReady}
              title={!isViewerReady ? t.viewer.imageViewLoading : t.viewer.imageViewTitle}
              className="flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-200 shadow-lg"
              style={{
                background: isViewerReady ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.25)',
                backdropFilter: 'blur(8px) saturate(140%)',
                WebkitBackdropFilter: 'blur(8px) saturate(140%)',
                border:     '1px solid rgba(255,255,255,0.1)',
                color: isViewerReady ? 'white' : 'rgba(0,0,0,0.35)',
                fontWeight: 600,
                
                cursor: isViewerReady ? 'pointer' : 'not-allowed',
              }}
            >
              {!isViewerReady && (
                <Loader2
                  className="animate-spin"
                  style={{ width: '14px', height: '14px', opacity: 0.5 }}
                />
              )}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="14" rx="2"/>
                  <path d="M8 21h8"/>
                  <path d="M12 17v4"/>
              </svg>
              <span>{t.viewer.imageViewBtn}</span>
            </button>
          </div>

          {/* Branding */}
          <div className="fixed bottom-4 left-4 z-[1002] flex flex-col items-start">
            <span className="text-xs text-white/40 tracking-wide mb-1">
              {t.viewer.poweredBy}
            </span>

            <div className="flex items-center gap-2">
              <img
                src={logo}
                alt="CR3ATE.AI"
                className="h-8 w-8 object-contain opacity-70"
              />
              <h1 className="font-light text-lg text-[#0CB4E4] opacity-60 font-brand">
                cr3ate.ai
              </h1>
            </div>
          </div>

      
        {/* Navigation Map */}
        
          {!loading && !error && pathNetwork && pathNetwork.length > 0 && cameraPosition && (
            <div className="fixed bottom-4 right-4 z-40">  {/* Removed padding! */}
              <PathNetworkMiniMap
                key="path-network-map"
                pathNetwork={pathNetwork}
                currentPosition={{
                  latitude: cameraPosition.latitude,
                  longitude: cameraPosition.longitude
                }}
                //heading={cameraPosition.heading}
                allPois={assetsVisible ? filteredPois : []}
                showPOIs={assetsVisible}
                selectedPoiId={selectedPoiId}
                onPoiClick={handlePoiMapClick}
                onMapClick={handleMiniMapClick}
                mapNavTarget={mapNavTarget ?? undefined}
              />
            </div>
          )}

          {/* Measure Tool — dropdown anchored to the Measure button */}
          {!loading && !error && measureActive && (
            <MeasureTool
              viewerRef={viewerRef}
              isActive={measureActive}
              onClose={() => setMeasureActive(false)}
              anchorRef={measureBtnRef}
            />
          )}

          {/* Source Images — Mount component */}
          {!loading && !error && sourceImagesActive && (
            <SourceImages
              viewerRef={viewerRef}
              manifest={manifest}
              sceneId={scene?.sceneId}
              isActive={sourceImagesActive}
              onClose={() => setSourceImagesActive(false)}
              anchorRef={sourceImagesBtnRef}
            />
          )}

          {/* POI Overlay — filtered by active layers */}
          {!loading && !error && assetsVisible && (
            <POIOverlay
              viewerRef={viewerRef}
              pois={filteredPois}
            />
          )}

          {/* Assets layer filter dropdown */}
          {!loading && !error && assetFilterOpen && (
            <AssetFilterPanel
              pois={pois}
              anchorRef={assetsBtnRef}
              onOk={handleAssetFilterOk}
              onClose={handleAssetFilterClose}
              activeLayers={activeLayers}
              onToggleLayer={handleToggleLayer}
              onShowOnlyLayer={handleShowOnlyLayer}
              onToggleAll={handleToggleAll}
            />
          )}
              {/* Navigation Panel Overlay - Glassmorphism Design */}
          {showNavPanel && (
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1001]"
              onClick={handleBackdropClick}
              style={{
                fontFamily: "'Inter', sans-serif"
              }}
            >
              <div 
                className="glass-panel relative w-full max-w-[620px] mx-6 p-8 md:p-10 rounded-[28px] text-white"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(4px) saturate(120%)',
                  WebkitBackdropFilter: 'blur(4px) saturate(120%)',
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)'
                }}
                onClick={handlePanelClick}
              >
                

                {/* Navigation Header */}
                <h1 className="text-2xl text-center mb-10 tracking-widest opacity-90" style={{ fontWeight: 300 }}>
                  {t.viewer.navTitle}
                </h1>

                <div className="flex flex-col md:flex-row items-stretch gap-10">

                  {/* Left Section: Look around */}
                  <div className="flex-1 flex flex-col items-center">
                    <h2 className="text-lg font-normal mb-8 opacity-80 h-8 flex items-center tracking-wide">
                      {t.viewer.navLookAround}
                    </h2>

                    {/* Mouse graphic */}
                    <div className="relative w-40 h-40 flex items-center justify-center mb-8">
                      {/* Mouse outline */}
                      <div 
                        className="mouse-outline"
                        style={{
                          width: '56px',
                          height: '90px',
                          border: '1.5px solid rgba(255, 255, 255, 0.4)',
                          borderRadius: '26px',
                          position: 'relative',
                          zIndex: 2
                        }}
                      >
                        {/* Mouse wheel */}
                        <div 
                          className="mouse-wheel"
                          style={{
                            width: '4px',
                            height: '8px',
                            background: 'white',
                            borderRadius: '1.5px',
                            position: 'absolute',
                            top: '18px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            opacity: 0.9
                          }}
                        />
                      </div>
                      
                      {/* Rotation ring - the arrow indicator */}
                      <div 
                        className="mouse-ring"
                        style={{
                          position: 'absolute',
                          width: '120px',
                          height: '120px',
                          border: '1.2px solid rgba(255, 255, 255, 0.25)',
                          borderLeftColor: 'transparent',
                          borderBottomColor: 'transparent',
                          borderRadius: '50%',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%) rotate(-25deg)',
                          zIndex: 1
                        }}
                      />
                    </div>

                    <p className="text-[12px] uppercase tracking-[0.25em] font-light text-white/40">
                      {t.viewer.navLookHint}
                    </p>
                  </div>

                  {/* Divider */}
                  <div 
                    className="hidden md:block w-px"
                    style={{
                      background: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)'
                    }}
                  />

                  {/* Right Section: Move */}
                  <div className="flex-1 flex flex-col items-center">
                    <h2 className="text-lg font-normal mb-8 opacity-80 h-8 flex items-center tracking-wide">
                      {t.viewer.navMove}
                    </h2>

                    {/* D-Pad with indicators */}
                    <div className="relative mb-8" style={{ width: '240px', height: '160px' }}>
                      
                      {/* North indicator */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 opacity-20 transition-opacity">
                        <svg width="12" height="8" viewBox="0 0 10 6" fill="white">
                          <path d="M5 0L10 6H0L5 0Z"/>
                        </svg>
                      </div>

                      {/* South indicator */}
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rotate-180 opacity-20 transition-opacity">
                        <svg width="12" height="8" viewBox="0 0 10 6" fill="white">
                          <path d="M5 0L10 6H0L5 0Z"/>
                        </svg>
                      </div>

                      {/* West indicator */}
                      <div className="absolute left-2 top-[112px] -translate-y-1/2 -rotate-90 opacity-20 transition-opacity">
                        <svg width="12" height="8" viewBox="0 0 10 6" fill="white">
                          <path d="M5 0L10 6H0L5 0Z"/>
                        </svg>
                      </div>

                      {/* East indicator */}
                      <div className="absolute right-2 top-[112px] -translate-y-1/2 rotate-90 opacity-20 transition-opacity">
                        <svg width="12" height="8" viewBox="0 0 10 6" fill="white">
                          <path d="M5 0L10 6H0L5 0Z"/>
                        </svg>
                      </div>

                      {/* Centered Key Grid */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="grid grid-cols-3 gap-3">
                          {/* Row 1 */}
                          <div />
                          <div 
                            className="w-[52px] h-[52px] rounded-xl flex items-center justify-center border transition-all"
                            style={{
                              background: 'rgba(255, 255, 255, 0.08)',
                              borderColor: 'rgba(255, 255, 255, 0.15)'
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                              <path d="m18 15-6-6-6 6"/>
                            </svg>
                          </div>
                          <div />

                          {/* Row 2 */}
                          <div 
                            className="w-[52px] h-[52px] rounded-xl flex items-center justify-center border transition-all"
                            style={{
                              background: 'rgba(255, 255, 255, 0.08)',
                              borderColor: 'rgba(255, 255, 255, 0.15)'
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                              <path d="m15 18-6-6 6-6"/>
                            </svg>
                          </div>
                          <div 
                            className="w-[52px] h-[52px] rounded-xl flex items-center justify-center border transition-all"
                            style={{
                              background: 'rgba(255, 255, 255, 0.08)',
                              borderColor: 'rgba(255, 255, 255, 0.15)'
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                              <path d="m6 9 6 6 6-6"/>
                            </svg>
                          </div>
                          <div 
                            className="w-[52px] h-[52px] rounded-xl flex items-center justify-center border transition-all"
                            style={{
                              background: 'rgba(255, 255, 255, 0.08)',
                              borderColor: 'rgba(255, 255, 255, 0.15)'
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                              <path d="m9 18 6-6-6-6"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="text-[12px] uppercase tracking-[0.25em] font-light text-white/40">
                      {t.viewer.navMoveHint}
                    </p>
                  </div>
                </div>

                {/* Footer Caption */}
                <div className="mt-12 text-center text-[13px] font-light text-white/30 tracking-widest">
                  {t.viewer.navAdvanced} <span className="text-white/60 font-normal">WASD</span> + <span className="text-white/60 font-normal">Q</span> ({t.viewer.navUp}) / <span className="text-white/60 font-normal">E</span> ({t.viewer.navDown})
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

const GeoAutomationViewerPage: React.FC = () => (
  <I18nProvider>
    <GeoAutomationViewer />
  </I18nProvider>
);

export default GeoAutomationViewerPage;
